import { InjectionToken, NgModule, APP_INITIALIZER } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { Routes, RouterModule, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { computeAppBaseHref } from './modules/shared/services/tenant-id.util';
import { MatIconRegistry } from '@angular/material/icon';
import { AppComponent } from './app.component';
import { firstValueFrom } from 'rxjs';

import { DashboardComponent } from './modules/estimativa/dashboard/dashboard/dashboard.component';
import { EstimativaComponent } from './modules/estimativa/estimativa/estimativa/estimativa.component';
import { DashboardModule } from './modules/estimativa/dashboard/dashboard.module';
import { EstimativaModule } from './modules/estimativa/estimativa/estimativa.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BackofficeModule } from './modules/backoffice/backoffice.module';
import { BackofficeComponent } from './modules/backoffice/backoffice/backoffice.component';
import { PerfilesComponent } from './modules/backoffice/perfiles/perfiles.component';
import { CatalogStatsComponent } from './modules/backoffice/catalog-stats/catalog-stats.component';

import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';

import { ResultContainerComponent } from './modules/estimativa/estimativa/result-container/result-container.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ProjectsComponent } from './modules/estimativa/dashboard/projects/projects.component';
import { ProjectComponent } from './modules/estimativa/dashboard/project/project.component';
import { AuthenticationComponent } from './modules/shared/authentication/authentication.component';
import { AuthGuard } from './modules/shared/services/auth.guard';
import { GcmComponentComponent } from './modules/gcm/gcm-component/gcm-component.component';
import { GcmModule } from './modules/gcm/gcm.module';
import { UsersManagementComponent } from './modules/userManagement/users-management/users-management.component';
import { TenantsManagementComponent } from './modules/userManagement/tenants-management/tenants-management.component';
import { UserManagementModule } from './modules/userManagement/users-management.module';
import { TechsToolsManagementComponent } from './modules/techsToolsManagement/techs-tools-management/techs-tools-management.component';
import { TechsToolsManagementModule } from './modules/techsToolsManagement/techs-tools-management.module';
import { StatsComponent } from './modules/stats/stats/stats.component';
import { StatsModule } from './modules/stats/stats.module';
import { FaqsModule } from './modules/estimativa/faqs/faqs.module';
import { FaqsComponent } from './modules/estimativa/faqs/faqs/faqs.component';
import { ScrManagementComponent } from './modules/scrManagement/srcManagement/scr-management.component';
import { ScrManagementModule } from './modules/scrManagement/src-management.module';
import { CombinedResultEstimateComponent } from './modules/estimativa/dashboard/combined-result-estimate/combined-result-estimate.component';
import { NotificationsModule } from './modules/notifications/notifications.module';
import { NotificationsComponent } from './modules/notifications/notifications/notifications.component';
import { NotificationConfigComponent } from './modules/notifications/notification-config/notification-config.component';
import { NotFoundComponent } from './not-found.component';
import { SharedModule } from './modules/shared/shared.module';
import { WorkAreaContainerComponent } from './modules/shared/components/work-area-container/work-area-container.component';
import { ResetPasswordComponent } from './modules/shared/reset-password/reset-password.component';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from './modules/shared/services/auth.service';
import { PermissionService } from './modules/shared/services/permission.service';
import { Permission } from './modules/shared/security/permission';
import { permissionGuard } from './modules/shared/services/permission.guard';
import { estimativaEditGuard } from './modules/shared/services/estimativa-edit.guard';
import { tenantIdGuard } from './modules/shared/services/tenant-id.guard';
import { superAdminGuard } from './modules/shared/services/super-admin.guard';

function initializePermissions(permissionService: PermissionService, authService: AuthService) {
  return (): Promise<void> => {
    if (!authService.isAuthenticated()) {
      return Promise.resolve(); // No token → skip, don't block startup
    }
    return firstValueFrom(permissionService.loadPermissions());
  };
}

const routes: Routes = [
  {
    path: 'login',
    component: AuthenticationComponent,
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
  },
  {
    path: '',
    component: WorkAreaContainerComponent,
    canActivate: [AuthGuard, tenantIdGuard],
    children: [
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'management',
        component: BackofficeComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'perfiles',
        component: PerfilesComponent,
        canActivate: [AuthGuard, permissionGuard(Permission.PROFILE_ACCESS_SCREEN)]
      },
      {
        path: 'projectos',
        component: ProjectsComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'projecto',
        component: ProjectComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'projecto/result',
        component: CombinedResultEstimateComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'estimativa',
        component: EstimativaComponent,
        canActivate: [AuthGuard, estimativaEditGuard]
      },
      {
        path: 'estimativa/resultado',
        component: ResultContainerComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'estimativa/:id/resultado/',
        component: ResultContainerComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'estimativa/:id/resultado',
        component: ResultContainerComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'gcm',
        component: GcmComponentComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'users',
        component: UsersManagementComponent,
        canActivate: [AuthGuard, permissionGuard(Permission.USER_ACCESS_SCREEN)]
      },
      {
        path: 'tenants',
        component: TenantsManagementComponent,
        canActivate: [AuthGuard, superAdminGuard, permissionGuard(Permission.TENANT_MANAGE)]
      },
      {
        path: 'technologies',
        component: TechsToolsManagementComponent,
        canActivate: [AuthGuard, permissionGuard(Permission.TECHNOLOGY_ACCESS_SCREEN)]
      },
      {
        path: 'stats',
        component: StatsComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'catalog-stats',
        component: CatalogStatsComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'faqs',
        component: FaqsComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'scr',
        component: ScrManagementComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'notifications',
        component: NotificationsComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'notifications/configuration',
        component: NotificationConfigComponent,
        canActivate: [AuthGuard]
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      }
    ]
  },
  {
    path: '**',
    redirectTo: 'dashboard'
  }
];

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    SharedModule,
    BrowserModule,
    HttpClientModule,
    DashboardModule,
    RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' }),
    BrowserAnimationsModule,
    EstimativaModule,
    BackofficeModule,
    GcmModule,
    UserManagementModule,
    TechsToolsManagementModule,
    NgbCollapseModule,
    FontAwesomeModule,
    StatsModule,
    FaqsModule,
    ScrManagementModule,
    NotificationsModule,
    MatIconModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializePermissions,
      deps: [PermissionService, AuthService],
      multi: true
    },
    // Path-based multitenancy: when the URL contains /app/{slug}/..., set the
    // Angular Router base href to that prefix so every existing absolute
    // routerLink ("/dashboard", "/projectos", …) resolves under the tenant
    // slug without any per-component refactor. Without a slug, base href stays
    // at "/" so generic / login routes work as before.
    {
      provide: APP_BASE_HREF,
      useFactory: computeAppBaseHref
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    iconRegistry.addSvgIconResolver(name =>
      sanitizer.bypassSecurityTrustResourceUrl(`assets/svg/${name}.svg`)
    );
  }
}
